import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

// 本地 Tab 類型定義
export interface Tab {
  id: string;
  title: string;
  path: string;
  icon?: string;
  closable?: boolean;
  pinned?: boolean;
  component?: any;
  badge?: string | number;
  loading?: boolean;
  error?: string;
  lastAccessed?: number;
  customData?: Record<string, any>;
  // 動態 tab 相關屬性
  dynamic?: boolean;
  params?: Record<string, any>;
  meta?: Record<string, any>;
}

export interface TabConfig {
  maxTabs: number;
  enablePersistence: boolean;
  storageKey: string;
}

// 動態路由映射接口
export interface DynamicRouteMapping {
  path: string;
  title: string;
  icon?: string;
  parent?: string;
  dynamic?: boolean; // 標記是否為動態路由
  generateTitle?: (params: any) => string; // 動態生成標題的函數
  generateIcon?: (params: any) => string; // 動態生成圖標的函數
}

export const useTabStore = defineStore('tabs', () => {
  const tabs = ref<Tab[]>([]);
  const activeTabId = ref<string>('');
  const dynamicRouteMappings = ref<Map<string, DynamicRouteMapping>>(new Map());

  const config = ref<TabConfig>({
    maxTabs: 10,
    enablePersistence: true,
    storageKey: 'fms-tabs'
  });

  const activeTab = computed(() =>
    tabs.value.find((tab) => tab.id === activeTabId.value)
  );
  const hasTabs = computed(() => tabs.value.length > 0);

  // 註冊動態路由映射
  const registerDynamicRoute = (mapping: DynamicRouteMapping) => {
    dynamicRouteMappings.value.set(mapping.path, mapping);
  };

  // 批量註冊動態路由映射
  const registerDynamicRoutes = (mappings: DynamicRouteMapping[]) => {
    mappings.forEach((mapping) => {
      registerDynamicRoute(mapping);
    });
  };

  // 獲取動態路由映射
  const getDynamicRouteMapping = (
    path: string
  ): DynamicRouteMapping | undefined => {
    return dynamicRouteMappings.value.get(path);
  };

  // 動態生成 tab 標題
  const generateDynamicTitle = (path: string, params?: any): string => {
    const mapping = getDynamicRouteMapping(path);
    if (mapping?.generateTitle && params) {
      return mapping.generateTitle(params);
    }
    return mapping?.title || '未命名頁面';
  };

  // 動態生成 tab 圖標
  const generateDynamicIcon = (path: string, params?: any): string => {
    const mapping = getDynamicRouteMapping(path);
    if (mapping?.generateIcon && params) {
      return mapping.generateIcon(params);
    }
    return mapping?.icon || 'mdi-file';
  };

  // 構建動態麵包屑路徑
  const buildDynamicBreadcrumb = (path: string, params?: any): string => {
    const breadcrumbItems: string[] = [];

    const buildChain = (currentPath: string): void => {
      const mapping = getDynamicRouteMapping(currentPath);
      if (!mapping) return;

      // 如果有父級，先處理父級
      if (mapping.parent) {
        buildChain(mapping.parent);
      }

      // 添加當前項目
      const title =
        mapping.dynamic && params
          ? generateDynamicTitle(currentPath, params)
          : mapping.title;
      breadcrumbItems.push(title);
    };

    buildChain(path);
    return breadcrumbItems.length > 0 ? breadcrumbItems.join(' > ') : '首頁';
  };

  const saveToStorage = () => {
    if (!config.value.enablePersistence) return;

    try {
      const data = {
        tabs: tabs.value,
        activeTabId: activeTabId.value
        // 注意：不保存動態路由映射，因為它們應該在運行時重新註冊
      };
      localStorage.setItem(
        config.value.storageKey || 'fms-tabs',
        JSON.stringify(data)
      );
    } catch (error) {
      console.warn('Failed to save tabs to storage:', error);
    }
  };

  const loadFromStorage = () => {
    if (!config.value.enablePersistence) return;

    try {
      const data = localStorage.getItem(config.value.storageKey || 'fms-tabs');
      if (data) {
        const parsed = JSON.parse(data);

        // 檢查數據結構是否正確
        if (parsed.tabs && Array.isArray(parsed.tabs)) {
          // 驗證動態 tab 是否仍然有效
          const validTabs = parsed.tabs.filter((tab: Tab) => {
            if (tab.dynamic) {
              // 檢查動態路由映射是否仍然存在
              return getDynamicRouteMapping(tab.path);
            }
            return true;
          });

          tabs.value = validTabs;
          activeTabId.value = parsed.activeTabId || '';
        } else {
          // 如果數據結構不正確，清理舊數據
          console.warn('Invalid tab data structure, clearing old data');
          localStorage.removeItem(config.value.storageKey || 'fms-tabs');
          tabs.value = [];
          activeTabId.value = '';
        }
      }
    } catch (error) {
      console.warn('Failed to load tabs from storage:', error);
      // 清理損壞的數據
      localStorage.removeItem(config.value.storageKey || 'fms-tabs');
    }
  };

  // 初始化時加載數據
  const initializeStore = () => {
    if (config.value.enablePersistence) {
      loadFromStorage();
    }
  };

  // 立即初始化
  initializeStore();

  const addTab = (tab: Tab) => {
    const existingTab = tabs.value.find((t) => t.path === tab.path);
    if (existingTab) {
      setActiveTab(existingTab.id);
      return existingTab.id;
    }

    if (tabs.value.length >= (config.value.maxTabs || 10)) {
      removeTab(tabs.value[0].id);
    }

    tabs.value.push(tab);
    setActiveTab(tab.id);
    saveToStorage();
    return tab.id;
  };

  // 添加動態 tab
  const addDynamicTab = (path: string, params?: any, meta?: any) => {
    const existingTab = tabs.value.find((t) => t.path === path);
    if (existingTab) {
      setActiveTab(existingTab.id);
      return existingTab.id;
    }

    if (tabs.value.length >= (config.value.maxTabs || 10)) {
      removeTab(tabs.value[0].id);
    }

    const dynamicTab: Tab = {
      id: `dynamic-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title: generateDynamicTitle(path, params),
      path,
      icon: generateDynamicIcon(path, params),
      closable: true,
      dynamic: true,
      params,
      meta
    };

    tabs.value.push(dynamicTab);
    setActiveTab(dynamicTab.id);
    saveToStorage();
    return dynamicTab.id;
  };

  const removeTab = (tabId: string) => {
    const index = tabs.value.findIndex((tab) => tab.id === tabId);
    if (index === -1) return;

    tabs.value.splice(index, 1);

    if (activeTabId.value === tabId) {
      if (tabs.value.length > 0) {
        setActiveTab(tabs.value[Math.min(index, tabs.value.length - 1)].id);
      } else {
        activeTabId.value = '';
      }
    }

    saveToStorage();
  };

  const setActiveTab = (tabId: string) => {
    if (tabs.value.find((tab) => tab.id === tabId)) {
      activeTabId.value = tabId;
      saveToStorage();
    }
  };

  const clearTabs = () => {
    tabs.value = [];
    activeTabId.value = '';
    saveToStorage();
  };

  const setConfig = (newConfig: Partial<TabConfig>) => {
    config.value = { ...config.value, ...newConfig };
  };

  const enablePersistence = (enabled = true) => {
    config.value.enablePersistence = enabled;
    if (enabled) {
      loadFromStorage();
    }
  };

  const setMaxTabs = (max: number) => {
    config.value.maxTabs = max;
  };

  return {
    tabs,
    activeTabId,
    config,
    dynamicRouteMappings,

    activeTab,
    hasTabs,

    // 動態路由相關方法
    registerDynamicRoute,
    registerDynamicRoutes,
    getDynamicRouteMapping,
    generateDynamicTitle,
    generateDynamicIcon,
    buildDynamicBreadcrumb,

    // Tab 操作方法
    addTab,
    addDynamicTab,
    removeTab,
    setActiveTab,
    clearTabs,
    setConfig,
    enablePersistence,
    setMaxTabs,
    saveToStorage,
    loadFromStorage
  };
});
